import React, { useState, useEffect } from 'react';
import {
    Activity,
    Layers,
    ChevronDown,
    ChevronUp,
    Filter
} from 'lucide-react';
import CustomSimpleDateRange from "../form/CustomSimpleDateRange";
import {getMain} from "../../api/dashboard/dashboard.api";
import LoadingBlock from "../LoadingBlock";
import {useRecoilValue} from "recoil";
import {accountSelector} from "../../store/selector/selector";
import {clearStoredPeriod, defaultPeriod, readStoredPeriod, storePeriod} from "../../libs/dashboardPeriod";

const Dashboard = () => {
    // 1. 기간 및 API 연동용 상태 관리
    // 관리자가 채널을 바꿔 가며 같은 기간을 보도록, 관리자가 고른 기간은 기억한다(libs/dashboardPeriod.js, 2026-10-01).
    const account = useRecoilValue(accountSelector);
    const isSystemAdmin = account?.isSystemAdmin === true;
    const [initialPeriod] = useState(() => readStoredPeriod() ?? defaultPeriod());
    const [startDate, setStartDate] = useState(initialPeriod.start);
    const [endDate, setEndDate] = useState(initialPeriod.end);
    // 기간 선택 컴포넌트는 처음 받은 값으로만 초기화된다 → '이번 달로' 를 누르면 key 를 바꿔 다시 그린다.
    const [pickerKey, setPickerKey] = useState(0);
    const [usingStored, setUsingStored] = useState(() => readStoredPeriod() !== null);
    const [branchPerformances, setBranchPerformances] = useState([]);
    const [branches, setBranches] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    // 조회 중에는 빈 표 대신 스피너를 보여준다(데이터 없음과 구분되지 않던 문제, 2026-08-05).
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    // 지점별 아코디언 펼침/닫힘 상태 (기본적으로 강남본점만 펼쳐둠)
    const [expandedBranches, setExpandedBranches] = useState({});

    // 지점 내 발생 유형 필터 상태 ('전체', '1회성', '자동화', '이벤트')
    const [branchFilters, setBranchFilters] = useState({});
    // 적립·테스트·기타 발송(서버 `auxiliary=true` 행) 표시 여부. 기본은 숨김 — 이 화면은 '통합 성과'(캠페인·자동화·이벤트)가
    // 본류다. 숨겨도 상단 '메시지 발송' 은 전체 발송 그대로이므로, 숨긴 건수를 토글 옆에 함께 보인다(2026-10-01).
    const [showAux, setShowAux] = useState(false);

    useEffect(() => {
        if (startDate && endDate) {
            const formatDate = (date) => {
                const yyyy = date.getFullYear();
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                return `${yyyy}-${mm}-${dd}`;
            };

            setLoading(true);
            setLoadError("");
            getMain(formatDate(startDate), formatDate(endDate)).then(({data}) => {
              setBranchPerformances(data.groupingEntryPointStatistics);
              setBranches(data.branches)
              setCampaigns(data.campaigns)
            }).catch((e) => {
              setBranchPerformances([])
              setBranches([])
              setCampaigns([])
              setLoadError(e?.message || "조회 중 오류가 발생했습니다.")
            }).finally(() => setLoading(false));
        }
    }, [startDate, endDate]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('ko-KR').format(amount) + '원';
    };

    const toggleBranchAccordion = (branchName) => {
        setExpandedBranches(prev => ({ ...prev, [branchName]: !prev[branchName] }));
    };

    const setFilterType = (branchName, filterType) => {
        setBranchFilters(prev => ({ ...prev, [branchName]: filterType }));
    };


    // 지점별로 그룹화 및 합계 계산
    const auxRows = campaigns.filter(item => item.auxiliary);
    const auxCount = auxRows.reduce((acc, item) => acc + (item.msgCount ?? 0), 0);
    const visibleCampaigns = showAux ? campaigns : campaigns.filter(item => !item.auxiliary);

    const groupedByBranch = branches.map(branchName => {
        const items = visibleCampaigns.filter(item => item.branch === branchName);
        const totals = items.reduce((acc, curr) => ({
            msgCost: acc.msgCost + curr.msgCost,
            msgCount: acc.msgCount + curr.msgCount,
            benCost: acc.benCost + curr.benCost,
            totalCost: acc.totalCost + (curr.msgCost + curr.benCost),
            totalCount: acc.totalCount + curr.totalCount,
            useCount: acc.useCount + curr.useCount,
        }), { msgCost: 0, msgCount: 0, benCost: 0, totalCost: 0, totalCount: 0, useCount: 0 });

        return { branch: branchName, items, totals };
    }).filter(group => group.items.length > 0);

    const filterOptions = ['전체', '1회성', '자동화', '이벤트'];

    return (
        <div className="w-full flex flex-col gap-6 bg-slate-50">

            {/* 타이틀 및 기간 선택 */}
            <div className="flex flex-wrap items-center justify-between gap-4 w-full bg-white rounded-3xl shadow-sm border border-slate-100 px-6 py-4">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-slate-100 text-slate-700 rounded-full">
                        <Activity size={24} />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-slate-800">통합 비용 및 성과 대시보드</h2>
                        <p className="text-sm text-slate-400 font-medium">본사 및 지점별 예산 귀속 현황</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {isSystemAdmin && usingStored && (
                        <button
                            type="button"
                            className="text-xs text-slate-400 hover:text-slate-600 underline whitespace-nowrap"
                            title="관리자가 마지막으로 고른 기간이 유지되고 있습니다. 누르면 이번 달(1일~오늘)로 돌아갑니다."
                            onClick={() => {
                                const p = defaultPeriod();
                                clearStoredPeriod();
                                setUsingStored(false);
                                setStartDate(p.start);
                                setEndDate(p.end);
                                setPickerKey((k) => k + 1);
                            }}
                        >이번 달로</button>
                    )}
                    {
                        (startDate && endDate) && (
                            <CustomSimpleDateRange key={pickerKey}
                                                   initStartDate={startDate}
                                                   initEndDate={endDate}
                                                   onChangeDate={(startDate, endDate) => {
                                                        setStartDate(startDate)
                                                        setEndDate(endDate)
                                                        if (isSystemAdmin && startDate && endDate) {
                                                            storePeriod(startDate, endDate)
                                                            setUsingStored(true)
                                                        }
                                                        console.log(startDate, endDate)
                                                    }}
                            />
                        )
                    }
                </div>
            </div>

            {/* 0. AI 데이터 분석 창 */}
            {/*
            <div className="w-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-3xl shadow-sm p-6 flex flex-col relative overflow-hidden">
                <div className="flex items-center gap-2 mb-2 relative z-10">
                    <Sparkles size={20} className="text-indigo-100" />
                    <h3 className="text-white font-bold text-lg">AI 비용 효율 분석</h3>
                </div>
                <div className="relative z-10 text-white/90 text-sm leading-relaxed max-w-5xl">
                    <p>
                        "이번 달 전체 예산 <strong className="text-yellow-300">9,790,000원</strong> 중 본사 지원 비중이 약 58%를 차지하고 있습니다.
                        특히 <strong className="text-indigo-100">신규가입 웰컴 패키지(본사 지원)</strong>의 혜택 사용률이 가장 높으며,
                        전체 재방문 고객은 총 {revisits.toLocaleString()}명입니다."
                    </p>
                </div>
            </div>
            */}
            {/* 1. 상단 요약 카드 */}
            {/*
            <div className="flex flex-wrap gap-4 w-full">
                <div className="flex-1 min-w-[280px] bg-white rounded-3xl shadow-sm border border-slate-100 p-6 flex flex-col gap-4">
                    <div className="flex items-center gap-2 text-indigo-600 mb-2">
                        <Building2 size={20} />
                        <span className="font-bold">본사 지원 비용</span>
                    </div>
                    <div className="flex justify-between items-end border-b border-slate-50 pb-3">
                        <span className="text-sm text-slate-500 font-medium">메시지 예산</span>
                        <span className="text-xl font-bold text-slate-700">{formatCurrency(summary.hqMessage)}</span>
                    </div>
                    <div className="flex justify-between items-end">
                        <span className="text-sm text-slate-500 font-medium">혜택 예산</span>
                        <span className="text-xl font-bold text-slate-700">{formatCurrency(summary.hqBenefit)}</span>
                    </div>
                </div>

                <div className="flex-1 min-w-[280px] bg-white rounded-3xl shadow-sm border border-slate-100 p-6 flex flex-col gap-4">
                    <div className="flex items-center gap-2 text-emerald-600 mb-2">
                        <Store size={20} />
                        <span className="font-bold">지점 자체 부담</span>
                    </div>
                    <div className="flex justify-between items-end border-b border-slate-50 pb-3">
                        <span className="text-sm text-slate-500 font-medium">발송 예산</span>
                        <span className="text-xl font-bold text-slate-700">{formatCurrency(summary.storeMessage)}</span>
                    </div>
                    <div className="flex justify-between items-end">
                        <span className="text-sm text-slate-500 font-medium">제공 혜택</span>
                        <span className="text-xl font-bold text-slate-700">{formatCurrency(summary.storeBenefit)}</span>
                    </div>
                </div>

                <div className="flex-[1.5] min-w-[320px] bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl shadow-sm border border-blue-100 p-6 flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/60 rounded-full blur-3xl pointer-events-none"></div>
                    <div className="flex gap-4 w-full relative z-10">
                        <div className="flex-1 flex flex-col justify-center">
                            <div className="flex items-center gap-2 mb-3 text-blue-600">
                                <Wallet size={20} />
                                <span className="font-bold text-sm">통합 집행 비용</span>
                            </div>
                            <span className="text-3xl font-bold text-slate-800">{formatCurrency(summary.totalCost)}</span>
                        </div>
                        <div className="w-px bg-blue-200/50 mx-2"></div>
                        <div className="flex-1 flex flex-col justify-center pl-2">
                            <div className="flex items-center gap-2 mb-3 text-rose-500">
                                <Users size={20} />
                                <span className="font-bold text-sm">첫방문 성공 고객</span>
                            </div>
                            <div className="flex items-end gap-1">
                                <span className="text-3xl font-bold text-slate-800">{revisits.toLocaleString()}</span>
                                <span className="text-slate-500 mb-1 font-medium">명</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
*/}
            {(loading || loadError) ? (
                <div className="w-full bg-white rounded-3xl shadow-sm border border-slate-100">
                    <LoadingBlock error={loadError} text="성과 데이터를 불러오는 중입니다…" />
                </div>
            ) : (
              <>
            {/* 2. 지점 성과 리스트 */}
            <div className="w-full bg-white rounded-3xl shadow-sm border border-slate-100 p-6">
                <div className="flex items-center gap-2 mb-6">
                    <Activity size={20} className="text-slate-700" />
                    <h3 className="text-slate-800 font-bold text-lg">지점별 종합 성과 및 부담 비율</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 text-slate-500 rounded-xl">
                        <tr>
                            <th className="px-5 py-4 font-semibold first:rounded-l-2xl">지점명</th>
                            <th className="px-5 py-4 font-semibold text-right">혜택 발급</th>
                            <th className="px-5 py-4 font-semibold text-right text-blue-600">사용 건수</th>
                            <th className="px-5 py-4 font-semibold text-right">혜택 사용율</th>
                            <th className="px-5 py-4 font-semibold text-right text-blue-600">메시지 발송</th>
                            <th className="px-5 py-4 font-semibold text-right">메시지 비용</th>
                            <th className="px-5 py-4 font-semibold text-right">혜택 비용</th>
                            <th className="px-5 py-4 font-semibold text-right text-slate-800">비용 합계</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                        {branchPerformances.map((branch, index) => (
                            <tr key={index} className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-5 py-4 font-bold text-slate-700">{branch.branch}</td>
                                <td className="px-5 py-4 text-right text-slate-500">{branch.benefitCount.toLocaleString()}</td>
                                <td className="px-5 py-4 text-right font-semibold text-blue-600">{branch.usedBenefitCount.toLocaleString()}</td>
                                <td className="px-5 py-4 text-right font-medium text-slate-600">{branch.benefitRate.toFixed(2)}%</td>
                                <td className="px-5 py-4 text-right font-semibold text-blue-600">{branch.totalMessageCount.toLocaleString()}</td>
                                <td className="px-5 py-4 text-right text-slate-600">{formatCurrency(branch.totalMessagePrice)}</td>
                                <td className="px-5 py-4 text-right font-semibold text-slate-700">{formatCurrency(branch.totalBenefitPrice)}</td>
                                <td className="px-5 py-4 text-right font-semibold text-slate-800">{formatCurrency(branch.totalMessagePrice + branch.totalBenefitPrice)}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* 3. 비용 귀속 현황 상세 내역 (수정됨) */}
            <div className="w-full bg-white rounded-3xl shadow-sm border border-slate-100 p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <Layers size={20} className="text-slate-700" />
                        <h3 className="text-slate-800 font-bold text-lg">지점별 상세 캠페인 귀속 현황</h3>
                    </div>
                    <span className="text-xs text-slate-400 font-medium">지점을 클릭하여 캠페인 리스트를 확인하세요.</span>
                </div>
                {/* 하단 합계 = 상단 '메시지 발송'(2026-10-01). 진입점에 나눠 담을 수 없는 발송도 행으로 보여 숫자가 사라지지 않게 한다. */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400 -mt-4 mb-4">
                    <span>
                        <b className="text-slate-500">진입점 미확인</b> = 받은 고객의 가입 진입점을 확인할 수 없는 자동화 발송(삭제된 진입점 등)
                    </span>
                    {auxRows.length > 0 && (
                        <label className="flex items-center gap-1.5 cursor-pointer text-slate-500">
                            <input type="checkbox" checked={showAux} onChange={(e) => setShowAux(e.target.checked)} />
                            적립·테스트·기타 발송 보기
                            <span className="text-slate-400">({auxCount.toLocaleString()}건{showAux ? '' : ' 숨김 — 상단 메시지 발송에는 포함'})</span>
                        </label>
                    )}
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="text-slate-500">
                        <tr className="border-b-2 border-slate-100">
                            <th className="px-4 py-3 font-semibold w-1/4">지점명 / 캠페인</th>
                            <th className="px-4 py-3 font-semibold text-center w-[10%]">발생 유형</th>
                            {/*<th className="px-4 py-3 font-semibold text-center w-[10%]">부담 주체</th>*/}
                            <th className="px-4 py-3 font-semibold text-right">혜택 발급</th>
                            <th className="px-4 py-3 font-semibold text-right text-blue-600">사용 건수</th>
                            <th className="px-4 py-3 font-semibold text-right">혜택 사용율</th>
                            <th className="px-4 py-3 font-semibold text-right text-blue-600">메시지 발송</th>
                            <th className="px-4 py-3 font-semibold text-right">메시지 비용</th>
                            <th className="px-4 py-3 font-semibold text-right">혜택 비용</th>
                            <th className="px-4 py-3 font-semibold text-right text-slate-800">비용 합계</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                        {groupedByBranch.map((group) => {
                            const isExpanded = expandedBranches[group.branch];
                            const activeFilter = branchFilters[group.branch] || '전체';

                            // 선택된 필터에 따라 아이템 필터링
                            const filteredItems = activeFilter === '전체'
                                ? group.items
                                : group.items.filter(item => item.type === activeFilter);

                            return (
                                <React.Fragment key={group.branch}>
                                    {/* 아코디언 헤더 (지점명 및 총합계 요약) */}
                                    <tr
                                        onClick={() => toggleBranchAccordion(group.branch)}
                                        className={`cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}
                                    >
                                        <td className="px-4 py-4 font-bold text-slate-800 flex items-center gap-2">
                                            {isExpanded ? <ChevronUp size={18} className="text-slate-400"/> : <ChevronDown size={18} className="text-slate-400"/>}
                                            <span className="text-[15px]">{group.branch}</span>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <span className="text-xs font-semibold text-slate-400">총 {group.items.length}개</span>
                                        </td>
                                        {/*<td className="px-4 py-4 text-center text-slate-400">-</td>*/}
                                        <td className="px-4 py-4 text-right font-bold">{group.totals.totalCount.toLocaleString()}건</td>
                                        <td className="px-4 py-4 text-right font-bold text-blue-600">{group.totals.useCount.toLocaleString()}건</td>
                                        <td className="px-4 py-4 text-right font-bold text-slate-500">{ group.totals.totalCount === 0 ? (0).toFixed(2) : (group.totals.useCount / group.totals.totalCount * 100).toFixed(2)}%</td>
                                        <td className="px-4 py-4 text-right font-medium text-blue-600">{group.totals.msgCount.toLocaleString()}</td>
                                        <td className="px-4 py-4 text-right font-medium text-slate-500">{formatCurrency(group.totals.msgCost)}</td>
                                        <td className="px-4 py-4 text-right font-medium text-slate-500">{formatCurrency(group.totals.benCost)}</td>
                                        <td className="px-4 py-4 text-right font-bold text-slate-800">{formatCurrency(group.totals.totalCost)}</td>
                                    </tr>

                                    {/* 아코디언 콘텐츠: 필터 탭 (해당 지점이 펼쳐졌을 때만 보임) */}
                                    {isExpanded && (
                                        <tr className="bg-slate-50/50">
                                            <td colSpan={8} className="px-10 py-3">
                                                <div className="flex items-center gap-3">
                                                    <Filter size={16} className="text-slate-400" />
                                                    <div className="flex gap-2 bg-white p-1 rounded-2xl border border-slate-100 shadow-sm">
                                                        {filterOptions.map(filter => (
                                                            <button
                                                                key={filter}
                                                                onClick={(e) => { e.stopPropagation(); setFilterType(group.branch, filter); }}
                                                                className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all ${
                                                                    activeFilter === filter
                                                                        ? 'bg-slate-700 text-white shadow-sm'
                                                                        : 'bg-transparent text-slate-500 hover:bg-slate-50'
                                                                }`}
                                                            >
                                                                {filter}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {/* 아코디언 콘텐츠: 필터링된 캠페인 리스트 */}
                                    {isExpanded && filteredItems.map((item) => (
                                        <tr key={item.id} className="bg-white hover:bg-slate-50/30 transition-colors border-l-4 border-l-transparent hover:border-l-indigo-300">
                                            <td className="px-4 py-3 pl-12">
                                                <span className="font-semibold text-slate-600">{item.name}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                  <span className={`px-2.5 py-1 text-[11px] font-bold rounded-lg ${
                                                      item.type === '자동화' ? 'bg-purple-100 text-purple-600' :
                                                          item.type === '이벤트' ? 'bg-amber-100 text-amber-600' :
                                                              'bg-blue-100 text-blue-600'
                                                  }`}>
                                                    {item.type}
                                                  </span>
                                            </td>
                                            {/*
                                            <td className="px-4 py-3 text-center">
                                              <span className={`px-2 py-0.5 text-[11px] font-bold rounded border ${
                                                  item.payer === '본사' ? 'border-indigo-200 text-indigo-500 bg-indigo-50' : 'border-emerald-200 text-emerald-500 bg-emerald-50'
                                              }`}>
                                                {item.payer}
                                              </span>
                                            </td>
                                            */}
                                            <td className="px-4 py-3 text-right text-sm font-medium">{item.totalCount.toLocaleString()}건</td>
                                            <td className="px-4 py-3 text-right text-sm font-medium text-blue-500">{item.useCount.toLocaleString()}건</td>
                                            <td className="px-4 py-3 text-right text-sm font-medium text-slate-400">{item.benefitRate.toFixed(2)}%</td>
                                            <td className="px-4 py-3 text-right text-sm text-blue-500">{item.msgCount.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-right text-sm text-slate-400">{formatCurrency(item.msgCost)}</td>
                                            <td className="px-4 py-3 text-right text-sm text-slate-400">{formatCurrency(item.benCost)}</td>
                                            <td className="px-4 py-3 text-right text-sm font-semibold text-slate-600">{formatCurrency(item.msgCost + item.benCost)}</td>
                                        </tr>
                                    ))}

                                    {/* 만약 해당 유형에 리스트가 없을 경우 */}
                                    {isExpanded && filteredItems.length === 0 && (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-400 bg-white">
                                                해당 발생 유형의 캠페인 내역이 없습니다.
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </div>
              </>
            )}

        </div>
    );
};

export default Dashboard;